import { hexToBytes, splitResponses } from './dtc.ts';

// Reading the VIN is mode 09 PID 02. It is the single most fragile read in
// OBD-II because 17 bytes never fit one CAN frame, so the reply always comes
// back segmented and every adapter formats those segments a little
// differently.

const VIN_CHARS = /^[A-HJ-NPR-Z0-9]+$/;

export function parseVinResponse(raw: string): string | null {
  const responses = splitResponses(raw);

  // More than one module can answer mode 09. Take the first reply that yields
  // a well-formed VIN rather than merging them, which would splice two
  // different strings together.
  for (const hex of responses) {
    const vin = vinFromHex(hex);
    if (vin) return vin;
  }

  // K-line vehicles are the exception: they spread one VIN across several
  // lines, each carrying its own 4902<index> header. Those really are a single
  // message, so merge them only after the per-response attempt has failed and
  // only when every line looks like part of the same sequence.
  const sequence = responses.filter((r) => r.startsWith('4902'));
  if (sequence.length > 1) return vinFromHex(sequence.join(''));

  return null;
}

function vinFromHex(hex: string): string | null {
  {
    if (!hex) return null;

    // Each response block starts 49 02 <index>. Strip every one of them; the VIN
    // is ASCII in the 0x30-0x5A range, so the byte pair 49 02 can never occur
    // inside the payload itself.
    const payload = hex.replace(/4902[0-9A-F]{2}/g, '');

    const ascii = hexToBytes(payload)
      .map((b) => String.fromCharCode(b))
      .join('');

    // Drop padding nulls and any stray control characters, then take the run of
    // valid VIN characters.
    const candidate = ascii.replace(/[^A-HJ-NPR-Z0-9]/gi, '').toUpperCase();
    if (candidate.length < 17) return null;

    // Manufacturers occasionally pad the front rather than the back.
    const vin = candidate.slice(-17);
    return VIN_CHARS.test(vin) ? vin : null;
  }
}

/** Format for display in 3 groups, which is how VIN plates are usually read aloud. */
export function formatVin(vin: string): string {
  if (vin.length !== 17) return vin;
  return `${vin.slice(0, 3)} ${vin.slice(3, 9)} ${vin.slice(9)}`;
}
